/**
 * Element ids: one byte per cell (docs/spec/02). Creature and explosion ids are consecutive runs,
 * so a direction or a stage is an offset from the first of the run.
 */
export const E = {
  SPACE: 0,
  DIRT: 1,
  BRICK: 2,
  MAGIC_WALL: 3,
  STEEL: 4,
  INBOX: 5,
  /** The exit before the quota: drawn exactly like steel. */
  OUTBOX_CLOSED: 6,
  OUTBOX_OPEN: 7,
  BOULDER: 8,
  BOULDER_FALLING: 9,
  DIAMOND: 10,
  DIAMOND_FALLING: 11,
  DIGBY: 12,
  /** Fireflies heading left, up, right, down (the order of `CDIR`). */
  FIREFLY: 13,
  BUTTERFLY: 17,
  AMOEBA: 21,
  /** Explosion stages 1-5, ending in space. */
  EXPLODE: 22,
  /** Explosion stages 1-5 ending in a diamond (a butterfly's). */
  DIAMOND_BIRTH: 27,
  /** Digby's birth stages 1-3 after the inbox. */
  BIRTH: 32,
  EXPANDING_WALL: 35,
  SLIME: 36,
  /** Keys gold, jade, ruby. */
  KEY: 37,
  /** Doors gold, jade, ruby. */
  DOOR: 40,
  DOOR_OPENING: 43,
  CLOCK: 44,
} as const;

export const ELEMENT_COUNT = 45;
export const EXPLODE_STAGES = 5;
export const BIRTH_STAGES = 3;

/** Creature headings in clockwise order: turning right is +1, turning left is +3 (mod 4). */
export const CDIR_LEFT = 0;
export const CDIR_UP = 1;
export const CDIR_RIGHT = 2;
export const CDIR_DOWN = 3;
export const CDX: readonly number[] = [-1, 0, 1, 0];
export const CDY: readonly number[] = [0, -1, 0, 1];

function table(ids: readonly number[]): Uint8Array {
  const t = new Uint8Array(ELEMENT_COUNT);
  for (const id of ids) t[id] = 1;
  return t;
}

function run(first: number, count: number): number[] {
  return Array.from({ length: count }, (_, i) => first + i);
}

/** Things roll off these (02 §2). */
export const ROUNDED = table([E.BRICK, E.BOULDER, E.DIAMOND, E.EXPANDING_WALL, E.CLOCK]);

/** A falling boulder or diamond landing on these makes them explode. */
export const EXPLODES_ON_HIT = table([E.DIGBY, ...run(E.FIREFLY, 4), ...run(E.BUTTERFLY, 4)]);

/** Untouched by explosions. */
export const EXPLOSION_PROOF = table([E.STEEL, E.INBOX, E.OUTBOX_CLOSED, E.OUTBOX_OPEN, ...run(E.DOOR, 3)]);

/** Amoeba grows into these. */
export const AMOEBA_EATS = table([E.SPACE, E.DIRT]);

export function isFirefly(el: number): boolean {
  return el >= E.FIREFLY && el < E.FIREFLY + 4;
}

export function isButterfly(el: number): boolean {
  return el >= E.BUTTERFLY && el < E.BUTTERFLY + 4;
}

/** Elements only found in the Deep set and the Construction Kit (02 §7.3). */
export const DEEP_ONLY = table([E.EXPANDING_WALL, E.SLIME, ...run(E.KEY, 3), ...run(E.DOOR, 3), E.DOOR_OPENING, E.CLOCK]);

const NAMES: Record<number, string> = {
  [E.SPACE]: 'space',
  [E.DIRT]: 'dirt',
  [E.BRICK]: 'brick wall',
  [E.MAGIC_WALL]: 'magic wall',
  [E.STEEL]: 'steel wall',
  [E.INBOX]: 'inbox',
  [E.OUTBOX_CLOSED]: 'exit (closed)',
  [E.OUTBOX_OPEN]: 'exit',
  [E.BOULDER]: 'boulder',
  [E.BOULDER_FALLING]: 'boulder (falling)',
  [E.DIAMOND]: 'diamond',
  [E.DIAMOND_FALLING]: 'diamond (falling)',
  [E.DIGBY]: 'digby',
  [E.AMOEBA]: 'amoeba',
  [E.EXPANDING_WALL]: 'expanding wall',
  [E.SLIME]: 'slime',
  [E.DOOR_OPENING]: 'door (opening)',
  [E.CLOCK]: 'clock',
};

const COLOURS = ['gold', 'jade', 'ruby'];

/** A readable name for debugging and tests. */
export function elementName(el: number): string {
  const named = NAMES[el];
  if (named !== undefined) return named;
  if (isFirefly(el)) return `firefly ${el - E.FIREFLY}`;
  if (isButterfly(el)) return `butterfly ${el - E.BUTTERFLY}`;
  if (el >= E.EXPLODE && el < E.EXPLODE + EXPLODE_STAGES) return `explosion ${el - E.EXPLODE + 1}`;
  if (el >= E.DIAMOND_BIRTH && el < E.DIAMOND_BIRTH + EXPLODE_STAGES) return `diamond birth ${el - E.DIAMOND_BIRTH + 1}`;
  if (el >= E.BIRTH && el < E.BIRTH + BIRTH_STAGES) return `birth ${el - E.BIRTH + 1}`;
  if (el >= E.KEY && el < E.KEY + 3) return `${COLOURS[el - E.KEY]} key`;
  if (el >= E.DOOR && el < E.DOOR + 3) return `${COLOURS[el - E.DOOR]} door`;
  return `element ${el}`;
}
